// Light/dark theme: a `data-theme="dark"` attribute on <html> drives the CSS
// token overrides in styles.css. The initial attribute is applied by the inline
// script in index.html (before React mounts) to avoid a flash; this module keeps
// the in-app toggle, persistence, and OS-preference fallback consistent with it.

export type Theme = "light" | "dark";

const STORAGE_KEY = "ac-theme";

/** Read the persisted choice, or fall back to the OS preference. */
export function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through */
  }
  if (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

/** Reflect the theme onto <html> so the CSS overrides apply. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "dark") {
    root.setAttribute("data-theme", "dark");
  } else {
    root.removeAttribute("data-theme");
  }
}

/** Persist + apply a theme choice. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore persistence failures */
  }
  applyTheme(theme);
}
